import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

interface YearContextValue {
    year: number;
    setYear: (year: number) => void;
    yearQuery: string;
    yearOptions: { label: string; value: string }[];
}

const YearContext = createContext<YearContextValue | null>(null);

function buildYearQuery(year: number): string {
    const from = new Date(year, 0, 1, 0, 0, 0, 0).toISOString();
    const to = new Date(year, 11, 31, 23, 59, 59, 999).toISOString();
    return `?dateFrom=${encodeURIComponent(from)}&dateTo=${encodeURIComponent(to)}`;
}

export function YearProvider({ children }: { children: ReactNode }) {
    const currentYear = useMemo(() => new Date().getFullYear(), []);
    const [year, setYear] = useState<number>(currentYear);

    const value = useMemo<YearContextValue>(
        () => ({
            year,
            setYear,
            yearQuery: buildYearQuery(year),
            yearOptions: Array.from({ length: 6 }, (_, i) => currentYear - i).map((y) => ({
                label: String(y),
                value: String(y),
            })),
        }),
        [year, currentYear],
    );

    return <YearContext.Provider value={value}>{children}</YearContext.Provider>;
}

export function useYear(): YearContextValue {
    const ctx = useContext(YearContext);
    if (!ctx) throw new Error('useYear must be used within YearProvider');
    return ctx;
}
